import { useEffect, useId, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import { cadLatenessLabel, UNKNOWN_EVENT_LABEL, type AgentVersions } from "../api/parse.ts";
import { API_VERSION_STUB, PORTAL_VERSION, loadMenuStamp } from "./shipStamp.ts";
import {
  GRANT_PERMISSIONS,
  permissionCopy,
  RECORDING_VISIBLE_HELP,
  type DirectoryRole,
  type DirectoryUser,
  type GrantScope,
  type RoleGrant,
  type ScopeCatalog,
} from "./access.ts";
import { publishedAgentLabel } from "./agentRelease.ts";
import { auditDetailFields } from "./auditDetailFields.ts";
import { EMPTY_AUDIT_FILTERS, auditRowVisible, readAuditFilters, writeAuditFilters, type AuditFilters } from "./auditQuery.ts";
import {
  AUDIT_COLUMN_LABELS,
  AUDIT_FILTER_COLUMNS,
  DEFAULT_AUDIT_COLUMN_SORT,
  NO_AUDIT_COLUMN_FILTERS,
  auditFilterDisplayLabel,
  createAuditFacetMemo,
  hasAuditColumnFilters,
  selectedAuditColumnFilterCount,
  sortAuditRows,
  toggleAuditColumnFilter,
  type AuditColumnFilters,
  type AuditColumnSort,
  type AuditFilterColumn,
  type AuditRow,
  type AuditTableContext,
} from "./auditTableState.ts";
import { ChipPicker, TagEditor } from "./chipPicker.tsx";
import { useDialogFocusTrap } from "./dialogFocus.ts";
import { ColumnFilter, FilterChips, type FilterChip, type FilterChoice } from "./columnFilter.tsx";
import { DataTable, type DataColumn } from "./dataTable.tsx";
import { IconAction } from "./iconAction.tsx";
import { AUDIT_TABLE_ID, SETTINGS_ROLES_TABLE_ID, SETTINGS_USERS_TABLE_ID } from "./tableState.ts";
import { formatSessionWhen } from "./sessionWhen.ts";
import {
  DEFAULT_ROLES_SORT,
  NO_ROLES_FILTERS,
  ROLES_COLUMN_LABELS,
  ROLES_FILTER_COLUMNS,
  createRolesFacetMemo,
  hasRolesFilters,
  selectedRolesFilterCount,
  sortRoleRows,
  toggleRolesFilter,
  type RolesFilterColumn,
  type RolesFilters,
  type RolesSort,
} from "./rolesTableState.ts";
import {
  DEFAULT_USERS_SORT,
  NO_USERS_FILTERS,
  USERS_COLUMN_LABELS,
  USERS_FILTER_COLUMNS,
  createUsersFacetMemo,
  hasUsersFilters,
  selectedUsersFilterCount,
  sortUserRows,
  toggleUsersFilter,
  userStatusFacet,
  type UsersFilterColumn,
  type UsersFilters,
  type UsersSort,
} from "./usersTableState.ts";

const SEED_ADMIN = "andy@wylosolutions.com";

export function useDialog(): RefObject<HTMLDialogElement | null> {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const node = dialog.current;
    if (!node || node.open) return;
    if (typeof node.showModal === "function") node.showModal();
    else node.setAttribute("open", "");
  }, []);
  return dialog;
}

export function closeDialog(node: HTMLDialogElement | null) {
  if (!node) return;
  if (typeof node.close === "function") node.close();
  else node.dispatchEvent(new Event("close"));
}

function protectedSeed(user: DirectoryUser): boolean {
  return user.seeded && user.userName.trim().toLowerCase() === SEED_ADMIN;
}

function sameAccount(left: string, right: string): boolean {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

function sourceLabel(user: DirectoryUser): string {
  if (user.source === "seeded") return "Seeded";
  if (user.source === "scim") return "SCIM";
  return "Manual";
}

export function SettingsShell(props: {
  page: "overview" | "devices" | "users" | "roles" | "recording";
  showDirectory?: boolean;
  showRecording?: boolean;
  children: ReactNode;
}) {
  const verify = import.meta.env.DEV ? new URLSearchParams(window.location.search).get("wyloVerify") : null;
  function href(target: string): string {
    if (!verify) return target;
    return `${target}?wyloVerify=${encodeURIComponent(verify)}`;
  }
  const showDirectory = props.showDirectory !== false;
  const links: Array<{ page: typeof props.page; href: string; label: string }> = [
    { page: "overview", href: href("/settings"), label: "Overview" },
    { page: "devices", href: href("/settings/devices"), label: "Devices" },
    ...(showDirectory
      ? [
          { page: "users" as const, href: href("/settings/users"), label: "Users" },
          { page: "roles" as const, href: href("/settings/roles"), label: "Roles" },
        ]
      : []),
    ...(props.showRecording
      ? [{ page: "recording" as const, href: href("/settings/policies"), label: "Policies" }]
      : []),
  ];
  return (
    <div className="settings-shell">
      <nav className="settings-nav" aria-label="Settings">
        {links.map((link) => (
          <a key={link.href} href={link.href} aria-current={props.page === link.page ? "page" : undefined}>
            {link.label}
          </a>
        ))}
      </nav>
      <div className="settings-main">{props.children}</div>
    </div>
  );
}

export function SettingsOverview(props: {
  apiBase: string;
  installerPending: boolean;
  installerError: string | null;
  macNote: string | null;
  versions: AgentVersions | null;
  onDownloadWindows: () => void;
  onDownloadMac: () => void;
}) {
  const [apiVersion, setApiVersion] = useState(API_VERSION_STUB);
  const [live, setLive] = useState<string | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    void loadMenuStamp(props.apiBase, controller.signal).then((stamp) => {
      if (controller.signal.aborted) return;
      setApiVersion(stamp.apiVersion);
      setLive(stamp.live);
    });
    return () => controller.abort();
  }, [props.apiBase]);
  return (
    <section className="panel">
      <header className="panel-head">
        <h2>Overview</h2>
      </header>
      <h3>Version Info</h3>
      <p>Portal {PORTAL_VERSION}</p>
      <p>API {apiVersion}</p>
      <p>Windows agent {publishedAgentLabel(props.versions?.windows)}</p>
      <p>macOS agent {publishedAgentLabel(props.versions?.macos)}</p>
      {live !== null ? <p className="ship-live">{live}</p> : null}
      <h3>Agent Installers</h3>
      <div className="actions">
        <button type="button" disabled={props.installerPending} onClick={props.onDownloadWindows}>
          Download Windows x64
        </button>
        <button type="button" onClick={props.onDownloadMac}>
          Download macOS Apple silicon
        </button>
      </div>
      {props.installerError ? <p className="banner error">{props.installerError}</p> : null}
      {props.macNote ? <p>{props.macNote}</p> : null}
    </section>
  );
}

type UserWrite = { userName: string; displayName: string; roles: string[]; disabled: boolean };

function activeAdminCount(users: DirectoryUser[]): number {
  return users.filter((user) => !user.disabled && user.roles.includes("Global Admin")).length;
}

function roleNameMatches(left: string, right: string): boolean {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

function userHasRole(user: DirectoryUser, roleName: string): boolean {
  return user.roles.some((name) => roleNameMatches(name, roleName));
}

function roleMemberUsers(users: readonly DirectoryUser[], roleName: string): DirectoryUser[] {
  return users.filter((user) => userHasRole(user, roleName));
}

function rolesAfterMembership(user: DirectoryUser, roleName: string, assign: boolean): string[] {
  if (assign) {
    if (userHasRole(user, roleName)) return [...user.roles];
    return [...user.roles, roleName];
  }
  return user.roles.filter((name) => !roleNameMatches(name, roleName));
}

export function UsersScreen(props: {
  users: DirectoryUser[];
  roles?: DirectoryRole[];
  notice: string | null;
  canManage?: boolean;
  selfUserName?: string | null;
  onSetDisabled: (userId: string, disabled: boolean) => void;
  onCreate?: (input: { userName: string; displayName: string; roles: string[] }) => void;
  onSave?: (userId: string, input: UserWrite) => void;
  onRemove?: (userId: string) => void;
}) {
  const canManage = props.canManage === true;
  const lastAdmin = activeAdminCount(props.users) <= 1;
  const roleNames = (props.roles ?? []).map((role) => role.name);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [filters, setFilters] = useState<UsersFilters>(NO_USERS_FILTERS);
  const [sort, setSort] = useState<UsersSort>(DEFAULT_USERS_SORT);
  const facetMemo = useMemo(() => createUsersFacetMemo(), []);
  const facets = facetMemo(props.users, filters);
  const sorted = useMemo(() => sortUserRows(facets.rows, sort), [facets.rows, sort]);
  const editing = props.users.find((user) => user.id === editingId) ?? null;

  function toggle(column: UsersFilterColumn, value: string) {
    setFilters(toggleUsersFilter(filters, column, value));
  }

  function onSort(key: string) {
    const column = USERS_FILTER_COLUMNS.find((item) => item === key);
    if (!column) return;
    setSort({ column, direction: sort.column === column && sort.direction === "asc" ? "desc" : "asc" });
  }

  function choices(column: UsersFilterColumn): FilterChoice[] {
    const facet = facets.columns.get(column);
    const selected = filters.values.get(column);
    return (facet?.options ?? []).map((option) => ({
      key: `value:${option.value}`,
      label: option.label,
      count: String(option.count),
      checked: selected?.has(option.value) === true,
      onToggle: () => toggle(column, option.value),
    }));
  }

  const chips: FilterChip[] = [];
  for (const column of USERS_FILTER_COLUMNS) {
    for (const value of filters.values.get(column) ?? []) {
      chips.push({
        key: `value:${column}:${value}`,
        column: USERS_COLUMN_LABELS[column],
        label: value,
        onRemove: () => toggle(column, value),
      });
    }
  }

  function filterFor(column: UsersFilterColumn) {
    return <ColumnFilter label={USERS_COLUMN_LABELS[column]} choices={() => choices(column)} selected={selectedUsersFilterCount(filters, column)} />;
  }

  const columns: DataColumn<DirectoryUser>[] = [
    {
      key: "user",
      label: USERS_COLUMN_LABELS.user,
      defaultWidth: 220,
      minWidth: 120,
      sortable: true,
      rowHeader: true,
      filter: filterFor("user"),
      cell: (user) => user.userName,
    },
    {
      key: "status",
      label: USERS_COLUMN_LABELS.status,
      defaultWidth: 120,
      minWidth: 96,
      sortable: true,
      filter: filterFor("status"),
      cell: (user) => (
        <>
          {userStatusFacet(user)}
          <span className="device-status">{sourceLabel(user)}</span>
        </>
      ),
    },
    {
      key: "roles",
      label: USERS_COLUMN_LABELS.roles,
      defaultWidth: 200,
      minWidth: 96,
      sortable: true,
      filter: filterFor("roles"),
      cell: (user) => user.roles.join(", "),
    },
    {
      key: "actions",
      label: "Actions",
      defaultWidth: 148,
      minWidth: 120,
      className: "device-actions",
      cell: (user) => {
        if (!canManage) return null;
        const holdsLastAdmin = lastAdmin && !user.disabled && user.roles.includes("Global Admin");
        const self = props.selfUserName ? sameAccount(props.selfUserName, user.userName) : false;
        const seed = protectedSeed(user);
        return (
          <div className="actions">
            <IconAction label="Edit" kind="edit" onClick={() => setEditingId(user.id)} />
            {seed ? null : (
              <>
                <IconAction
                  label={user.disabled ? "Enable" : "Disable"}
                  kind={user.disabled ? "enable" : "disable"}
                  disabled={holdsLastAdmin}
                  onClick={() => props.onSetDisabled(user.id, !user.disabled)}
                />
                {confirmingId === user.id ? (
                  <>
                    <span>Remove this user?</span>
                    <button
                      type="button"
                      onClick={() => {
                        setConfirmingId(null);
                        props.onRemove?.(user.id);
                      }}
                    >
                      Confirm remove
                    </button>
                    <button type="button" onClick={() => setConfirmingId(null)}>
                      Cancel
                    </button>
                  </>
                ) : (
                  <IconAction
                    label="Remove"
                    kind="delete"
                    disabled={holdsLastAdmin || self}
                    title={self ? "You cannot remove your own account." : "Remove"}
                    onClick={() => setConfirmingId(user.id)}
                  />
                )}
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <section className="panel admin-panel">
      <header className="panel-head">
        <div>
          <h2>Users</h2>
          <p>A seeded admin stays active and keeps Global Admin. The last Global Admin stays in place.</p>
        </div>
      </header>
      {props.notice ? <p className="banner error">{props.notice}</p> : null}
      {canManage ? <UserCreate onCreate={props.onCreate} /> : null}
      <FilterChips chips={chips} onClearAll={() => setFilters(NO_USERS_FILTERS)} />
      {props.users.length > 0 && sorted.length === 0 && hasUsersFilters(filters) ? <p>No users match these column filters.</p> : null}
      <div className="device-table-wrap">
        <DataTable
          className="device-table placement-table"
          rows={sorted}
          rowKey={(user) => user.id}
          columns={columns}
          sort={{ key: sort.column, direction: sort.direction }}
          onSort={onSort}
          tableId={SETTINGS_USERS_TABLE_ID}
        />
      </div>
      {editing ? (
        <UserEditDialog
          user={editing}
          roleNames={roleNames}
          lockAdmin={lastAdmin && !editing.disabled && editing.roles.includes("Global Admin")}
          locked={protectedSeed(editing)}
          onClose={() => setEditingId(null)}
          onSave={(input) => {
            props.onSave?.(editing.id, input);
            setEditingId(null);
          }}
        />
      ) : null}
    </section>
  );
}

function UserCreate(props: {
  onCreate?: (input: { userName: string; displayName: string; roles: string[] }) => void;
}) {
  const [userName, setUserName] = useState("");
  const [displayName, setDisplayName] = useState("");
  return (
    <form
      className="user-create"
      onSubmit={(event) => {
        event.preventDefault();
        props.onCreate?.({ userName, displayName, roles: [] });
        setUserName("");
        setDisplayName("");
      }}
    >
      <label>
        Email
        <input aria-label="New user email" value={userName} onChange={(event) => setUserName(event.target.value)} />
      </label>
      <label>
        Name
        <input aria-label="New user name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
      </label>
      <button type="submit">Add user</button>
    </form>
  );
}

function UserEditDialog(props: {
  user: DirectoryUser;
  roleNames: string[];
  lockAdmin: boolean;
  locked: boolean;
  onClose: () => void;
  onSave: (input: UserWrite) => void;
}) {
  const dialog = useDialog();
  const [userName, setUserName] = useState(props.user.userName);
  const [displayName, setDisplayName] = useState(props.user.displayName ?? "");
  const [roles, setRoles] = useState(props.user.roles);
  const [disabled, setDisabled] = useState(props.user.disabled);
  return (
    <dialog ref={dialog} className="grant-dialog" aria-labelledby="edit-user-title" onClose={props.onClose}>
      <form
        className="fields"
        onSubmit={(event) => {
          event.preventDefault();
          if (props.locked) return;
          props.onSave({ userName, displayName, roles, disabled });
        }}
      >
        <h2 id="edit-user-title">Edit user</h2>
        <label>
          Email
          <input aria-label={`Email for ${props.user.userName}`} value={userName} disabled={props.locked} onChange={(event) => setUserName(event.target.value)} />
        </label>
        <label>
          Name
          <input aria-label={`Name for ${props.user.userName}`} value={displayName} disabled={props.locked} onChange={(event) => setDisplayName(event.target.value)} />
        </label>
        <fieldset disabled={props.locked}>
          <legend>Roles</legend>
          <ChipPicker
            inputLabel={`Assign role for ${props.user.userName}`}
            addLabel="Assign role"
            selected={roles}
            choices={props.roleNames}
            allowCreate={false}
            disabled={props.locked}
            locked={props.lockAdmin ? ["Global Admin"] : []}
            removeLabel={(value) => `Remove role ${value} from ${props.user.userName}`}
            onChange={(next) => {
              if (props.lockAdmin && roles.some((role) => role.toLowerCase() === "global admin") && !next.some((role) => role.toLowerCase() === "global admin")) {
                return;
              }
              setRoles(next);
            }}
          />
        </fieldset>
        <label>
          Status
          <select
            aria-label={`Status for ${props.user.userName}`}
            value={disabled ? "disabled" : "active"}
            disabled={props.locked || props.lockAdmin}
            onChange={(event) => setDisabled(event.target.value === "disabled")}
          >
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
          </select>
        </label>
        <div className="actions">
          <button type="button" onClick={() => closeDialog(dialog.current)}>
            Cancel
          </button>
          <button type="submit" disabled={props.locked}>
            Save
          </button>
        </div>
      </form>
    </dialog>
  );
}

function RoleMembersDialog(props: {
  role: DirectoryRole;
  users: DirectoryUser[];
  onClose: () => void;
  onSetUserRoles: (userId: string, roles: string[]) => Promise<string | null>;
}) {
  const dialog = useDialog();
  const [pick, setPick] = useState("");
  const [error, setError] = useState<string | null>(null);
  const members = useMemo(() => roleMemberUsers(props.users, props.role.name), [props.users, props.role.name]);
  const available = useMemo(
    () => props.users.filter((user) => !userHasRole(user, props.role.name)).sort((left, right) => left.userName.localeCompare(right.userName)),
    [props.users, props.role.name],
  );

  async function apply(user: DirectoryUser, assign: boolean) {
    setError(null);
    const next = rolesAfterMembership(user, props.role.name, assign);
    const problem = await props.onSetUserRoles(user.id, next);
    if (problem) setError(problem);
  }

  async function addMember() {
    const user = available.find((row) => row.id === pick);
    if (!user) return;
    await apply(user, true);
    setPick("");
  }

  return (
    <dialog ref={dialog} className="grant-dialog" aria-labelledby="role-members-title" onClose={props.onClose}>
      <div className="fields">
        <h2 id="role-members-title">{`Members of ${props.role.name}`}</h2>
        {error ? <p className="banner error">{error}</p> : null}
        {members.length === 0 ? <p>No members yet.</p> : null}
        <ul className="role-member-list" aria-label={`Members of ${props.role.name}`}>
          {members.map((user) => (
            <li key={user.id}>
              <span>{user.userName}</span>
              <IconAction label={`Remove ${user.userName} from ${props.role.name}`} kind="delete" onClick={() => void apply(user, false)} />
            </li>
          ))}
        </ul>
        <label>
          Add member
          <select aria-label={`Add member to ${props.role.name}`} value={pick} onChange={(event) => setPick(event.target.value)}>
            <option value="">Choose a user</option>
            {available.map((user) => (
              <option key={user.id} value={user.id}>
                {user.userName}
              </option>
            ))}
          </select>
        </label>
        <div className="actions">
          <button type="button" disabled={pick.length === 0} onClick={() => void addMember()}>
            Add member
          </button>
          <button type="button" className="secondary" onClick={() => closeDialog(dialog.current)}>
            Close
          </button>
        </div>
      </div>
    </dialog>
  );
}

export function RolesScreen(props: {
  roles: DirectoryRole[];
  users?: DirectoryUser[];
  notice: string | null;
  canManage?: boolean;
  catalog?: ScopeCatalog;
  onCreate?: (input: { name: string; grants: RoleGrant[] }) => void;
  onSave?: (roleId: string, input: { name: string; grants: RoleGrant[] }) => void;
  onRemove?: (roleId: string) => void;
  onSetUserRoles?: (userId: string, roles: string[]) => Promise<string | null>;
}) {
  const canManage = props.canManage === true;
  const catalog = props.catalog ?? { organisations: [], departments: [] };
  const [editing, setEditing] = useState<DirectoryRole | "new" | null>(null);
  const [membersRole, setMembersRole] = useState<DirectoryRole | null>(null);
  const [filters, setFilters] = useState<RolesFilters>(NO_ROLES_FILTERS);
  const [sort, setSort] = useState<RolesSort>(DEFAULT_ROLES_SORT);
  const facetMemo = useMemo(() => createRolesFacetMemo(), []);
  const facets = facetMemo(props.roles, filters);
  const sorted = useMemo(() => sortRoleRows(facets.rows, sort), [facets.rows, sort]);

  function toggle(column: RolesFilterColumn, value: string) {
    setFilters(toggleRolesFilter(filters, column, value));
  }

  function onSort(key: string) {
    const column = ROLES_FILTER_COLUMNS.find((item) => item === key);
    if (!column) return;
    setSort({ column, direction: sort.column === column && sort.direction === "asc" ? "desc" : "asc" });
  }

  function choices(column: RolesFilterColumn): FilterChoice[] {
    const facet = facets.columns.get(column);
    const selected = filters.values.get(column);
    return (facet?.options ?? []).map((option) => ({
      key: `value:${option.value}`,
      label: option.label,
      count: String(option.count),
      checked: selected?.has(option.value) === true,
      onToggle: () => toggle(column, option.value),
    }));
  }

  const chips: FilterChip[] = [];
  for (const column of ROLES_FILTER_COLUMNS) {
    for (const value of filters.values.get(column) ?? []) {
      chips.push({
        key: `value:${column}:${value}`,
        column: ROLES_COLUMN_LABELS[column],
        label: value,
        onRemove: () => toggle(column, value),
      });
    }
  }

  function filterFor(column: RolesFilterColumn) {
    return <ColumnFilter label={ROLES_COLUMN_LABELS[column]} choices={() => choices(column)} selected={selectedRolesFilterCount(filters, column)} />;
  }

  const columns: DataColumn<DirectoryRole>[] = [
    {
      key: "role",
      label: ROLES_COLUMN_LABELS.role,
      defaultWidth: 180,
      minWidth: 120,
      sortable: true,
      rowHeader: true,
      filter: filterFor("role"),
      cell: (role) => role.name,
    },
    {
      key: "scope",
      label: ROLES_COLUMN_LABELS.scope,
      defaultWidth: 120,
      minWidth: 96,
      sortable: true,
      filter: filterFor("scope"),
      cell: (role) => role.scope,
    },
    {
      key: "actions",
      label: "Actions",
      defaultWidth: 132,
      minWidth: 120,
      className: "device-actions",
      cell: (role) => {
        if (!canManage) return null;
        const assigned = (props.users ?? []).some((user) => userHasRole(user, role.name));
        const locked = role.builtIn || assigned;
        const deleteTitle = role.builtIn
          ? "Built-in roles cannot be deleted."
          : assigned
            ? "This role is still assigned to a user."
            : undefined;
        return (
          <div className="actions">
            <IconAction label="Members" kind="members" onClick={() => setMembersRole(role)} />
            <IconAction label="Edit" kind="edit" onClick={() => setEditing(role)} />
            <IconAction label="Delete" kind="delete" disabled={locked} title={deleteTitle ?? "Delete"} onClick={() => props.onRemove?.(role.id)} />
          </div>
        );
      },
    },
  ];

  return (
    <section className="panel admin-panel">
      <header className="panel-head">
        <div>
          <h2>Roles</h2>
          <p>Built-in Global Admin cannot be deleted or demoted. A role still assigned to a user cannot be deleted.</p>
        </div>
      </header>
      {props.notice ? <p className="banner error">{props.notice}</p> : null}
      {canManage ? (
        <div className="user-create">
          <button type="button" onClick={() => setEditing("new")}>
            Add role
          </button>
        </div>
      ) : null}
      <FilterChips chips={chips} onClearAll={() => setFilters(NO_ROLES_FILTERS)} />
      {props.roles.length > 0 && sorted.length === 0 && hasRolesFilters(filters) ? <p>No roles match these column filters.</p> : null}
      <div className="device-table-wrap">
        <DataTable
          className="device-table placement-table"
          rows={sorted}
          rowKey={(role) => role.id}
          columns={columns}
          sort={{ key: sort.column, direction: sort.direction }}
          onSort={onSort}
          tableId={SETTINGS_ROLES_TABLE_ID}
        />
      </div>
      {editing ? (
        <RoleDialog
          role={editing === "new" ? null : editing}
          catalog={catalog}
          onClose={() => setEditing(null)}
          onSave={(input) => {
            if (editing === "new") props.onCreate?.(input);
            else props.onSave?.(editing.id, input);
            setEditing(null);
          }}
        />
      ) : null}
      {membersRole && props.onSetUserRoles ? (
        <RoleMembersDialog
          role={membersRole}
          users={props.users ?? []}
          onClose={() => setMembersRole(null)}
          onSetUserRoles={props.onSetUserRoles}
        />
      ) : null}
    </section>
  );
}

type GrantDraft = {
  permission: string;
  scope: "" | GrantScope;
  organisationId: string;
  departmentId: string;
  tags: { key: string; value: string }[];
};

function blankDraft(permission: string): GrantDraft {
  return { permission, scope: "", organisationId: "", departmentId: "", tags: [] };
}

function draftFromGrant(grant: RoleGrant): GrantDraft {
  return {
    permission: grant.permission,
    scope: grant.scope,
    organisationId: grant.organisationId ?? "",
    departmentId: grant.departmentId ?? "",
    tags: [],
  };
}

function tagDraft(rows: RoleGrant[]): GrantDraft | null {
  const tags = rows.filter((grant) => grant.scope === "tag");
  if (tags.length === 0) return null;
  return {
    permission: tags[0]!.permission,
    scope: "tag",
    organisationId: "",
    departmentId: "",
    tags: tags.flatMap((grant) => {
      const key = (grant.tagKey ?? "").trim();
      if (key.length === 0) return [];
      return [{ key, value: (grant.tagValue ?? "").trim() }];
    }),
  };
}

function rowsToDrafts(rows: RoleGrant[]): GrantDraft[] {
  const tags = tagDraft(rows);
  const others = rows.filter((grant) => grant.scope !== "tag").map(draftFromGrant);
  return tags ? [tags, ...others] : others;
}

function draftsFor(role: DirectoryRole | null): GrantDraft[] {
  const held = new Map<string, RoleGrant[]>();
  for (const grant of role?.grants ?? []) {
    const rows = held.get(grant.permission) ?? [];
    rows.push(grant);
    held.set(grant.permission, rows);
  }
  const drafts: GrantDraft[] = [];
  for (const spec of GRANT_PERMISSIONS) {
    const rows = held.get(spec.name) ?? [];
    if (rows.length === 0) drafts.push(blankDraft(spec.name));
    else drafts.push(...rowsToDrafts(rows));
    held.delete(spec.name);
  }
  for (const rows of held.values()) drafts.push(...rowsToDrafts(rows));
  return drafts;
}

function allowedScopes(permission: string): GrantScope[] {
  return GRANT_PERMISSIONS.find((spec) => spec.name === permission)?.scopes ?? ["root", "org", "department", "tag"];
}

const TAG_CONTROL = /[\u0000-\u001F\u007F]/;

function cleanTags(tags: { key: string; value: string }[]): { key: string; value: string }[] | "invalid" | "empty" {
  const cleaned: { key: string; value: string }[] = [];
  const seen = new Set<string>();
  for (const tag of tags) {
    const key = tag.key.trim();
    const value = tag.value.trim();
    if (key.length === 0 && value.length === 0) continue;
    if (key.length < 1 || key.length > 128 || value.length > 128 || TAG_CONTROL.test(key) || TAG_CONTROL.test(value)) return "invalid";
    const fold = `${key}\0${value}`;
    if (seen.has(fold)) continue;
    seen.add(fold);
    cleaned.push({ key, value });
  }
  return cleaned.length === 0 ? "empty" : cleaned;
}

function draftsToGrants(drafts: GrantDraft[]): { grants: RoleGrant[] } | { error: string } {
  const grants: RoleGrant[] = [];
  for (const draft of drafts) {
    if (draft.scope === "") continue;
    if (draft.scope === "root") {
      grants.push({ permission: draft.permission, scope: "root" });
      continue;
    }
    if (draft.scope === "org") {
      if (draft.organisationId.length === 0) return { error: "Choose a scope target." };
      grants.push({ permission: draft.permission, scope: "org", organisationId: draft.organisationId });
      continue;
    }
    if (draft.scope === "department") {
      if (draft.departmentId.length === 0) return { error: "Choose a scope target." };
      grants.push({ permission: draft.permission, scope: "department", departmentId: draft.departmentId });
      continue;
    }
    const tags = cleanTags(draft.tags);
    if (tags === "invalid") return { error: "Tag is invalid." };
    if (tags === "empty") return { error: "Choose a scope target." };
    for (const tag of tags) {
      grants.push({ permission: draft.permission, scope: "tag", tagKey: tag.key, tagValue: tag.value });
    }
  }
  return { grants };
}

function scopeControlLabel(drafts: GrantDraft[], index: number, kind: string): string {
  const permission = drafts[index]?.permission ?? "";
  const count = drafts.filter((row) => row.permission === permission).length;
  const nth = drafts.slice(0, index + 1).filter((row) => row.permission === permission).length;
  const suffix = count > 1 ? ` ${nth}` : "";
  return `${kind} for ${permission}${suffix}`;
}

function RoleDialog(props: {
  role: DirectoryRole | null;
  catalog: ScopeCatalog;
  onClose: () => void;
  onSave: (input: { name: string; grants: RoleGrant[] }) => void;
}) {
  const dialog = useDialog();
  const readOnly = props.role?.builtIn === true;
  const [name, setName] = useState(props.role?.name ?? "");
  const [drafts, setDrafts] = useState<GrantDraft[]>(() => draftsFor(props.role));
  const [error, setError] = useState<string | null>(null);
  function patch(index: number, next: Partial<GrantDraft>) {
    setDrafts((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, ...next } : row)));
  }
  return (
    <dialog ref={dialog} className="grant-dialog" aria-labelledby="role-dialog-title" onClose={props.onClose}>
      <form
        className="fields"
        onSubmit={(event) => {
          event.preventDefault();
          if (readOnly) return;
          const next = name.trim();
          if (next.length === 0) {
            setError("Enter a role name.");
            return;
          }
          const grants = draftsToGrants(drafts);
          if ("error" in grants) {
            setError(grants.error);
            return;
          }
          props.onSave({ name: next, grants: grants.grants });
        }}
      >
        <h2 id="role-dialog-title">{props.role ? "Edit role" : "Add role"}</h2>
        <label>
          Name
          <input aria-label="Role name" value={name} disabled={readOnly} onChange={(event) => setName(event.target.value)} />
        </label>
        {drafts.map((draft, index) => (
          <div className="grant-row" key={`${draft.permission}-${index}`}>
            <span className="perm-name" title={permissionCopy(draft.permission)}>
              {draft.permission}
              {draft.permission === "recording.visible" ? (
                <button type="button" className="perm-info" aria-label="About recording.visible" title={RECORDING_VISIBLE_HELP}>
                  i
                </button>
              ) : null}
            </span>
            <select
              aria-label={scopeControlLabel(drafts, index, "Scope")}
              value={draft.scope}
              disabled={readOnly}
              onChange={(event) => patch(index, { scope: event.target.value as GrantDraft["scope"] })}
            >
              <option value="">none</option>
              {allowedScopes(draft.permission).map((scope) => (
                <option key={scope} value={scope}>
                  {scope}
                </option>
              ))}
            </select>
            {draft.scope === "org" ? (
              <select
                aria-label={scopeControlLabel(drafts, index, "Organisation")}
                value={draft.organisationId}
                disabled={readOnly}
                onChange={(event) => patch(index, { organisationId: event.target.value })}
              >
                <option value="">Choose an organisation</option>
                {props.catalog.organisations.map((organisation) => (
                  <option key={organisation.id} value={organisation.id}>
                    {organisation.name}
                  </option>
                ))}
              </select>
            ) : null}
            {draft.scope === "department" ? (
              <select
                aria-label={scopeControlLabel(drafts, index, "Department")}
                value={draft.departmentId}
                disabled={readOnly}
                onChange={(event) => patch(index, { departmentId: event.target.value })}
              >
                <option value="">Choose a department</option>
                {props.catalog.departments.map((department) => {
                  const organisation = props.catalog.organisations.find((item) => item.id === department.organisationId);
                  const label = organisation ? `${organisation.name} / ${department.name}` : department.name;
                  return (
                    <option key={department.id} value={department.id}>
                      {label}
                    </option>
                  );
                })}
              </select>
            ) : null}
            {draft.scope === "tag" ? (
              <TagEditor
                deviceName={draft.permission}
                tags={draft.tags}
                choices={props.catalog.tags ?? []}
                disabled={readOnly}
                addLabel={scopeControlLabel(drafts, index, "Add tag")}
                onChange={(tags) => patch(index, { tags })}
              />
            ) : null}
          </div>
        ))}
        {error ? <p className="banner error">{error}</p> : null}
        <div className="actions">
          <button type="button" onClick={() => closeDialog(dialog.current)}>
            Cancel
          </button>
          <button type="submit" disabled={readOnly} title={readOnly ? "Built-in roles cannot be changed." : undefined}>
            Save
          </button>
        </div>
      </form>
    </dialog>
  );
}

export function AdminScreen(props: {
  users: DirectoryUser[];
  roles: DirectoryRole[];
  notice: string | null;
  canManage?: boolean;
  onSetDisabled: (userId: string, disabled: boolean) => void;
  onCreate?: (input: { userName: string; displayName: string; roles: string[] }) => void;
  onSave?: (userId: string, input: UserWrite) => void;
  onRemove?: (userId: string) => void;
}) {
  return (
    <>
      <UsersScreen
        users={props.users}
        roles={props.roles}
        notice={props.notice}
        canManage={props.canManage}
        onSetDisabled={props.onSetDisabled}
        onCreate={props.onCreate}
        onSave={props.onSave}
        onRemove={props.onRemove}
      />
      <RolesScreen roles={props.roles} users={props.users} notice={null} canManage={props.canManage} />
    </>
  );
}

function cadFollowUp(
  row: { type: string; occurredAt: string; requestId?: string | null },
  items: ReadonlyArray<{ type: string; occurredAt: string; requestId?: string | null }>,
): string | null {
  if (row.type !== "cad.sent" && row.type !== "cad.failed") return null;
  if (!row.requestId) return null;
  const request = items.find((item) => item.type === "cad.requested" && item.requestId === row.requestId);
  if (!request) return null;
  return cadLatenessLabel(row.occurredAt, request.occurredAt);
}

function AuditDetailDialog(props: {
  row: AuditRow;
  summaryText: string;
  deviceName: string;
  filterTimeZone?: string;
  onClose: () => void;
}) {
  const dialog = useDialog();
  const titleId = useId();
  useDialogFocusTrap(dialog, props.onClose);
  const fields = auditDetailFields(props.row, props.summaryText, props.deviceName, props.filterTimeZone);
  return (
    <dialog
      ref={dialog}
      className="grant-dialog"
      aria-labelledby={titleId}
      onClose={props.onClose}
      onCancel={(event) => {
        event.preventDefault();
        props.onClose();
      }}
    >
      <div className="fields device-details-dialog">
        <h2 id={titleId}>Audit event</h2>
        <dl className="device-details-list">
          {fields.map((field) => (
            <div key={field.key}>
              <dt>{field.label}</dt>
              <dd>{field.value}</dd>
            </div>
          ))}
        </dl>
        <div className="actions">
          <button type="button" className="secondary" onClick={() => closeDialog(dialog.current)}>
            Close
          </button>
        </div>
      </div>
    </dialog>
  );
}

export function AuditLog(props: {
  items: AuditRow[] | null;
  error: string | null;
  truncated?: boolean;
  filters?: AuditFilters;
  onFiltersChange?: (next: AuditFilters) => void;
  filterTimeZone?: string;
  deviceNameFor?: (deviceId: string | null) => string;
  deviceNamesToken?: string;
}) {
  const filterTimeZone = props.filterTimeZone;
  const deviceNameFor = props.deviceNameFor ?? ((deviceId) => (deviceId ? deviceId : "None"));
  const tableCtx = useMemo(
    (): AuditTableContext => ({
      timeZone: filterTimeZone,
      deviceNamesToken: props.deviceNamesToken ?? "",
      deviceNameFor,
    }),
    [filterTimeZone, props.deviceNamesToken, deviceNameFor],
  );
  const [local, setLocal] = useState<AuditFilters>(() => readAuditFilters(window.location.search));
  const [columnFilters, setColumnFilters] = useState<AuditColumnFilters>(NO_AUDIT_COLUMN_FILTERS);
  const [sort, setSort] = useState<AuditColumnSort>(DEFAULT_AUDIT_COLUMN_SORT);
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const facetMemo = useMemo(() => createAuditFacetMemo(), []);
  const filters = props.filters ?? local;
  function change(next: AuditFilters) {
    writeAuditFilters(next);
    if (props.onFiltersChange) props.onFiltersChange(next);
    else setLocal(next);
  }
  const addressFiltered = props.items?.filter((row) => auditRowVisible(row, filters, filterTimeZone)) ?? null;
  const facets = addressFiltered ? facetMemo(addressFiltered, columnFilters, tableCtx) : null;
  const sorted = useMemo(() => (facets ? sortAuditRows(facets.rows, sort, tableCtx) : []), [facets, sort, tableCtx]);
  const detailed = detailsId ? sorted.find((row) => row.eventId === detailsId) ?? props.items?.find((row) => row.eventId === detailsId) ?? null : null;

  function updateColumnFilters(next: AuditColumnFilters) {
    setColumnFilters(next);
  }

  function toggle(column: AuditFilterColumn, value: string) {
    updateColumnFilters(toggleAuditColumnFilter(columnFilters, column, value));
  }

  function onSort(key: string) {
    const column = AUDIT_FILTER_COLUMNS.find((item) => item === key);
    if (!column) return;
    setSort({ column, direction: sort.column === column && sort.direction === "asc" ? "desc" : "asc" });
  }

  function choices(column: AuditFilterColumn): FilterChoice[] {
    const facet = facets?.columns.get(column);
    const selected = columnFilters.values.get(column);
    return (facet?.options ?? []).map((option) => ({
      key: `value:${option.value}`,
      label: option.label,
      count: String(option.count),
      checked: selected?.has(option.value) === true,
      onToggle: () => toggle(column, option.value),
    }));
  }

  const chips: FilterChip[] = [];
  for (const column of AUDIT_FILTER_COLUMNS) {
    for (const value of columnFilters.values.get(column) ?? []) {
      chips.push({
        key: `value:${column}:${value}`,
        column: AUDIT_COLUMN_LABELS[column],
        label: auditFilterDisplayLabel(column, value),
        onRemove: () => toggle(column, value),
      });
    }
  }

  function filterFor(column: AuditFilterColumn) {
    return (
      <ColumnFilter label={AUDIT_COLUMN_LABELS[column]} choices={() => choices(column)} selected={selectedAuditColumnFilterCount(columnFilters, column)} />
    );
  }

  function summaryText(row: AuditRow): string {
    const follow = props.items ? cadFollowUp(row, props.items) : null;
    return follow ? `${row.detail} ${follow}` : row.detail;
  }

  const columns: DataColumn<AuditRow>[] = [
    {
      key: "when",
      label: AUDIT_COLUMN_LABELS.when,
      defaultWidth: 168,
      minWidth: 48,
      sortable: true,
      filter: filterFor("when"),
      cell: (row) => formatSessionWhen(row.occurredAt, undefined, filterTimeZone),
    },
    {
      key: "who",
      label: AUDIT_COLUMN_LABELS.who,
      defaultWidth: 160,
      minWidth: 48,
      sortable: true,
      filter: filterFor("who"),
      cell: (row) => row.who,
    },
    {
      key: "type",
      label: AUDIT_COLUMN_LABELS.type,
      defaultWidth: 140,
      minWidth: 48,
      sortable: true,
      filter: filterFor("type"),
      cell: (row) => (row.type === "unknown" ? UNKNOWN_EVENT_LABEL : row.type),
    },
    {
      key: "device",
      label: AUDIT_COLUMN_LABELS.device,
      defaultWidth: 140,
      minWidth: 48,
      sortable: true,
      filter: filterFor("device"),
      cell: (row) => deviceNameFor(row.deviceId),
    },
    {
      key: "session",
      label: AUDIT_COLUMN_LABELS.session,
      defaultWidth: 120,
      minWidth: 48,
      sortable: true,
      filter: filterFor("session"),
      cell: (row) => (row.sessionId ?? "None"),
    },
    {
      key: "summary",
      label: AUDIT_COLUMN_LABELS.summary,
      defaultWidth: 160,
      minWidth: 48,
      sortable: true,
      filter: filterFor("summary"),
      cell: (row) => summaryText(row),
    },
    {
      key: "actions",
      label: "Actions",
      defaultWidth: 72,
      minWidth: 56,
      className: "device-actions",
      cell: (row) => <IconAction label="Details" kind="details" onClick={() => setDetailsId(row.eventId)} />,
    },
  ];

  const noColumnMatches =
    addressFiltered !== null && addressFiltered.length > 0 && sorted.length === 0 && hasAuditColumnFilters(columnFilters);

  return (
    <section className="panel">
      <header className="panel-head">
        <h2>Audit Log</h2>
      </header>
      <div className="audit-filters">
        <label>
          From
          <input type="date" aria-label="When from" value={filters.from} onChange={(event) => change({ ...filters, from: event.target.value })} />
        </label>
        <label>
          To
          <input type="date" aria-label="When to" value={filters.to} onChange={(event) => change({ ...filters, to: event.target.value })} />
        </label>
        <button type="button" onClick={() => change(EMPTY_AUDIT_FILTERS)}>
          Clear dates
        </button>
      </div>
      {props.error ? <p className="banner error">{props.error}</p> : null}
      {props.truncated ? <p>Showing the newest 4,000 events; narrow the dates</p> : null}
      {props.items === null && props.error === null ? <p>Loading the audit log.</p> : null}
      {props.items !== null && props.items.length === 0 && !props.truncated ? <p>No audit events yet.</p> : null}
      {addressFiltered !== null && props.items !== null && props.items.length > 0 && addressFiltered.length === 0 ? (
        <p>No audit events match these dates.</p>
      ) : null}
      {noColumnMatches ? <p>No audit events match these column filters.</p> : null}
      <FilterChips chips={chips} onClearAll={() => updateColumnFilters(NO_AUDIT_COLUMN_FILTERS)} />
      {sorted.length > 0 ? (
        <div className="device-table-wrap">
          <DataTable
            className="device-table audit-table placement-table"
            rows={sorted}
            rowKey={(row) => row.eventId}
            columns={columns}
            sort={{ key: sort.column, direction: sort.direction }}
            onSort={onSort}
            tableId={AUDIT_TABLE_ID}
          />
        </div>
      ) : null}
      {detailed ? (
        <AuditDetailDialog
          row={detailed}
          summaryText={summaryText(detailed)}
          deviceName={deviceNameFor(detailed.deviceId)}
          filterTimeZone={filterTimeZone}
          onClose={() => setDetailsId(null)}
        />
      ) : null}
    </section>
  );
}

